import React, { useState, useEffect, useMemo } from "react";
import { addressMapApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./AddressMap.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { key: "preview", label: "Live Preview", icon: "🌐" },
  { key: "info", label: "Institution Info", icon: "🏛️" },
  { key: "address", label: "Location & Map", icon: "📍" },
  { key: "contact", label: "Contact Channels", icon: "📧" },
];

const BLANK_ADDRESS = {
  _id: null,
  Institution_Full_Name: "",
  Short_Name: "",
  Tagline: "",
  Address_Line_1: "",
  Address_Line_2: "",
  City: "",
  District: "",
  State: "",
  Pincode: "",
  Country: "India",
  Landmark: "",
  Latitude: "",
  Longitude: "",
  Primary_Phone: "",
  Secondary_Phone: "",
  Fax: "",
  General_Email: "",
  Admissions_Email: "",
  Principal_Email: "",
  Website_URL: "",
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AddressMap = () => {
  const [formData, setFormData] = useState(BLANK_ADDRESS);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("preview");
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    fetchAddress();
  }, []);

  const fetchAddress = async () => {
    try {
      setLoading(true);
      const res = await apiClient(addressMapApi);
      const json = await res.json();
      if (res.ok && json.data?.length > 0) {
        setFormData(json.data[0]);
      }
    } catch (err) {
      console.error("Error fetching address:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const isPost = !formData._id;
      const method = isPost ? "POST" : "PUT";
      const url = isPost ? addressMapApi : `${addressMapApi}/${formData._id}`;
      const res = await apiClient(url, {
        method,
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 3000);
        fetchAddress();
      }
    } catch (err) {
      console.error("Error saving address:", err);
    } finally {
      setLoading(false);
    }
  };

  const update = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="am-page">
      {/* ── Loading Overlay ── */}
      {loading && (
        <div className="am-loading-overlay">
          <div className="am-spinner" />
          <p>Syncing location settings...</p>
        </div>
      )}

      {/* ── Save Success Toast ── */}
      {isSaved && (
        <div className="am-toast">
          <span>✅</span> Settings updated successfully!
        </div>
      )}

      {/* ── Header ── */}
      <div className="am-header">
        <div className="am-header-left">
          <div className="am-header-icon">📍</div>
          <div>
            <div className="am-breadcrumb">
              <span>Media & Contact</span>
              <span>/</span>
              <span className="am-bc-active">Address & Map Settings</span>
            </div>
            <h1>Address & Map Settings</h1>
            <p>Configure the official geographical and digital contact endpoints for the college.</p>
          </div>
        </div>
        <div className="am-header-actions">
          <button className="am-btn-primary" onClick={handleSave}>
            Save All Changes
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="am-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`am-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="am-content">
        <form onSubmit={handleSave}>

          {/* ── Live Preview Tab ── */}
          {activeTab === "preview" && (
            <div className="am-tab-panel preview-mode">
              <div className="am-preview-header">
                <h3>Live Website Preview</h3>
                <p>This is how your contact information appears on the public website footer and contact page.</p>
              </div>

              <div className="am-mockup">
                <div className="am-mock-header">
                  <div className="am-mock-logo">🏛️ {formData.Short_Name || "COLLEGE"}</div>
                  <nav><span>Home</span> <span>Academics</span> <span>Contact</span></nav>
                </div>

                <div className="am-mock-contact-section">
                  <div className="am-mock-info">
                    <span className="am-mock-badge">OFFICIAL CONTACT</span>
                    <h2>{formData.Institution_Full_Name || "Institution Name"}</h2>
                    <p className="am-mock-tagline">{formData.Tagline || "Inspiring Excellence in Education"}</p>

                    <div className="am-mock-grid">
                      <div className="am-mock-item">
                        <span>📍 Find Us</span>
                        <strong>{formData.Address_Line_1}, {formData.Address_Line_2}, {formData.City}, {formData.State} - {formData.Pincode}</strong>
                      </div>
                      <div className="am-mock-item">
                        <span>📞 Call Us</span>
                        <strong>{formData.Primary_Phone} {formData.Secondary_Phone && ` / ${formData.Secondary_Phone}`}</strong>
                      </div>
                      <div className="am-mock-item">
                        <span>📧 Email Us</span>
                        <strong>{formData.General_Email}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="am-mock-map">
                    <div className="am-mock-map-inner">
                      {formData.Latitude && formData.Longitude ? (
                        <div className="am-mock-placeholder">
                          <span>🗺️</span>
                          <p>Map centered at {formData.Latitude}, {formData.Longitude}</p>
                        </div>
                      ) : (
                        <div className="am-mock-placeholder empty">
                          <p>No map coordinates set</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Institution Info Tab ── */}
          {activeTab === "info" && (
            <div className="am-tab-panel">
              <div className="am-section-card">
                <div className="am-card-head">
                  <h3>Institution Branding</h3>
                  <p>Update the official names and taglines used in headers and footers.</p>
                </div>
                <div className="am-card-body">
                  <div className="am-field full">
                    <label>Full Institution Name</label>
                    <input name="Institution_Full_Name" value={formData.Institution_Full_Name} onChange={update} placeholder="e.g. Rajat Group Of College" />
                  </div>
                  <div className="am-field-row">
                    <div className="am-field">
                      <label>Short Name / Acronym</label>
                      <input name="Short_Name" value={formData.Short_Name} onChange={update} placeholder="e.g. RPGC" />
                    </div>
                    <div className="am-field">
                      <label>Official Tagline</label>
                      <input name="Tagline" value={formData.Tagline} onChange={update} placeholder="e.g. Permanently Associated with Lucknow University" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Location Tab ── */}
          {activeTab === "address" && (
            <div className="am-tab-panel">
              <div className="am-section-card">
                <div className="am-card-head">
                  <h3>Physical Address</h3>
                  <p>Provide the precise location details for mailing and visitor guidance.</p>
                </div>
                <div className="am-card-body">
                  <div className="am-field-row">
                    <div className="am-field">
                      <label>Address Line 1</label>
                      <input name="Address_Line_1" value={formData.Address_Line_1} onChange={update} placeholder="e.g. Plot No. 123, Near Main Gate" />
                    </div>
                    <div className="am-field">
                      <label>Address Line 2</label>
                      <input name="Address_Line_2" value={formData.Address_Line_2} onChange={update} placeholder="e.g. Kamta Road, Chinhat" />
                    </div>
                  </div>
                  <div className="am-field-row three">
                    <div className="am-field"><label>City</label><input name="City" value={formData.City} onChange={update} /></div>
                    <div className="am-field"><label>District</label><input name="District" value={formData.District} onChange={update} /></div>
                    <div className="am-field"><label>State</label><input name="State" value={formData.State} onChange={update} /></div>
                  </div>
                  <div className="am-field-row">
                    <div className="am-field"><label>Pincode</label><input name="Pincode" value={formData.Pincode} onChange={update} /></div>
                    <div className="am-field"><label>Country</label><input name="Country" value={formData.Country} onChange={update} /></div>
                  </div>
                  <div className="am-field full">
                    <label>Landmark</label>
                    <input name="Landmark" value={formData.Landmark} onChange={update} placeholder="e.g. Opposite SBI Bank" />
                  </div>
                </div>
              </div>

              <div className="am-section-card mt">
                <div className="am-card-head">
                  <h3>Map Coordinates</h3>
                  <p>Used to pinpoint the college location on the website's interactive map.</p>
                </div>
                <div className="am-card-body">
                  <div className="am-field-row">
                    <div className="am-field"><label>Latitude</label><input name="Latitude" value={formData.Latitude} onChange={update} placeholder="e.g. 26.8467" /></div>
                    <div className="am-field"><label>Longitude</label><input name="Longitude" value={formData.Longitude} onChange={update} placeholder="e.g. 80.9462" /></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Contact Channels Tab ── */}
          {activeTab === "contact" && (
            <div className="am-tab-panel">
              <div className="am-section-card">
                <div className="am-card-head">
                  <h3>Communication Hub</h3>
                  <p>Official phone numbers and emails for public inquiries.</p>
                </div>
                <div className="am-card-body">
                  <div className="am-field-row">
                    <div className="am-field"><label>Primary Phone</label><input name="Primary_Phone" value={formData.Primary_Phone} onChange={update} /></div>
                    <div className="am-field"><label>Secondary Phone</label><input name="Secondary_Phone" value={formData.Secondary_Phone} onChange={update} /></div>
                  </div>
                  <div className="am-field-row">
                    <div className="am-field"><label>General Inquiry Email</label><input type="email" name="General_Email" value={formData.General_Email} onChange={update} /></div>
                    <div className="am-field"><label>Admissions Email</label><input type="email" name="Admissions_Email" value={formData.Admissions_Email} onChange={update} /></div>
                  </div>
                  <div className="am-field-row">
                    <div className="am-field"><label>Principal / Office Email</label><input type="email" name="Principal_Email" value={formData.Principal_Email} onChange={update} /></div>
                    <div className="am-field"><label>Official Website URL</label><input name="Website_URL" value={formData.Website_URL} onChange={update} /></div>
                  </div>
                  <div className="am-field full">
                    <label>Fax Number</label>
                    <input name="Fax" value={formData.Fax} onChange={update} />
                  </div>
                </div>
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default AddressMap;